import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { WarningCircle } from '@phosphor-icons/react';
import type { ShopifyUninstallImpact, ShopifyUninstallImpactCampaign } from '@discovr/types';
import { shopifyCopy, shopifyCountsCopy, type UninstallFailure } from '@discovr/features/copy';
import { useShopifySalesExport } from '@discovr/features/shopify';
import { StatusPill } from '../components/common/StatusPill';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { PlatformMark } from '../campaign-detail/components/campaignDetailsParts';
import { INVALID_FIELD_CLASS } from '../campaign-detail/components/shopifyInvalidField';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { REDUCED_MOTION_DIALOG_CLASS } from '../components/ui/modalScrim';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const copy = shopifyCopy.uninstall;
const VISIBLE_CAMPAIGNS = 2;
/** Failures where pressing Uninstall again can only fail the same way. */
const TERMINAL_FAILURES: ReadonlySet<UninstallFailure['kind']> = new Set([
  'in_progress',
  'access_lost',
  'not_connected',
  'forbidden',
  'still_working',
]);

export interface UninstallChoice {
  turnOffCodes: boolean;
  continueAnyway: boolean;
}

interface ShopifyUninstallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopDomain: string;
  /** Only meaningful once `impactLoading` and `impactError` are both false:
   *  until then an absent list does NOT mean "no campaigns affected". */
  impact?: ShopifyUninstallImpact;
  impactLoading?: boolean;
  impactError?: boolean;
  /** Uninstall in flight. Blocks every way of closing the dialog. */
  pending?: boolean;
  failure?: UninstallFailure;
  onConfirm: (choice: UninstallChoice) => void;
  /** The control that opened this. Without a `DialogTrigger` Radix has no
   *  trigger to restore focus to, so closing dropped focus to `<body>`. */
  returnFocusTo?: RefObject<HTMLElement | null>;
}

/**
 * Typed-confirm dialog for Discovr-initiated uninstall. Replaces the old
 * Disconnect dialog: it names the store, lists what is deleted, and offers to
 * turn the store's discount codes off first (on by default).
 */
export function ShopifyUninstallDialog({
  open,
  onOpenChange,
  shopDomain,
  impact,
  impactLoading,
  impactError,
  pending = false,
  failure,
  onConfirm,
  returnFocusTo,
}: ShopifyUninstallDialogProps) {
  const titleRef = useRef<HTMLHeadingElement>(null);

  return (
    <Dialog open={open} onOpenChange={(next) => !(pending && !next) && onOpenChange(next)}>
      <DialogContent
        data-clarity-mask="True"
        className={`flex max-h-[90dvh] flex-col sm:max-w-md ${REDUCED_MOTION_DIALOG_CLASS}`}
        showCloseButton={!pending}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          titleRef.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          if (!returnFocusTo?.current?.isConnected) return;
          e.preventDefault();
          returnFocusTo.current.focus();
        }}
        onEscapeKeyDown={(e) => pending && e.preventDefault()}
        onInteractOutside={(e) => pending && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle ref={titleRef} tabIndex={-1}>
            {copy.title}
          </DialogTitle>
          <DialogDescription>{copy.description(shopDomain)}</DialogDescription>
        </DialogHeader>
        {/* State lives inside the content so Radix unmounting it on close resets
            the typed word and checkbox without a setState-in-effect. */}
        <div data-slot="uninstall-body" className="-mx-2 min-h-0 flex-1 space-y-4 overflow-y-auto px-2 py-1">
          <UninstallDialogBody
            impact={impact}
            impactLoading={impactLoading}
            impactError={impactError}
            pending={pending}
            failure={failure}
            onConfirm={onConfirm}
            onCancel={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UninstallDialogBody({
  impact,
  impactLoading,
  impactError,
  pending,
  failure,
  onConfirm,
  onCancel,
}: {
  impact?: ShopifyUninstallImpact;
  impactLoading?: boolean;
  impactError?: boolean;
  pending: boolean;
  failure?: UninstallFailure;
  onConfirm: (choice: UninstallChoice) => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [turnOffCodes, setTurnOffCodes] = useState(true);
  const { download, pending: exporting } = useShopifySalesExport();
  const checkboxId = useId();
  const inputId = useId();
  const errorId = useId();

  const armed = typed.trim() === copy.confirmWord;
  const codesFailed = failure?.kind === 'codes_turn_off_failed';
  const blocked = !!failure && TERMINAL_FAILURES.has(failure.kind);
  const canSubmit = armed && !pending && !blocked;
  const campaigns = impact?.campaigns ?? [];

  const submit = (continueAnyway: boolean) => onConfirm({ turnOffCodes, continueAnyway });

  return (
    <>
      <ul className="bg-muted text-muted-foreground list-disc space-y-1.5 rounded-lg py-2.5 pr-3 pl-7 text-caption-regular">
        {copy.consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <div className="flex items-start gap-2">
        <Checkbox
          id={checkboxId}
          checked={turnOffCodes}
          disabled={pending}
          onCheckedChange={(v) => setTurnOffCodes(v === true)}
          className="mt-0.5"
        />
        <Label htmlFor={checkboxId} className="text-body-sm-regular/snug font-normal">
          {copy.turnOffCodes}
        </Label>
      </div>

      <CampaignsAffected
        campaigns={campaigns}
        counts={impact?.counts}
        loading={impactLoading}
        error={impactError}
      />

      <div className="space-y-2">
        <p className="text-muted-foreground text-body-sm-regular">{copy.downloadNote}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={exporting || pending}
          aria-busy={exporting}
          onClick={() => void download()}
        >
          {exporting ? copy.downloading : copy.downloadAll}
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={inputId} className="flex-wrap gap-0 select-text">
          {copy.confirmPrefix} &ldquo;
          <code className="bg-muted min-w-0 rounded-sm px-1 py-0.5 text-caption-medium break-all">
            {copy.confirmWord}
          </code>
          &rdquo; {copy.confirmSuffix}
        </Label>
        <Input
          id={inputId}
          value={typed}
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          placeholder={copy.confirmWord}
          readOnly={pending}
          aria-describedby={failure ? errorId : undefined}
          aria-invalid={failure?.kind === 'confirm_mismatch' ? true : undefined}
          className={INVALID_FIELD_CLASS}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSubmit && !codesFailed) submit(false);
          }}
        />
      </div>

      {failure ? (
        <FailureAlert
          key={`${failure.kind}:${failure.message}`}
          id={errorId}
          failure={failure}
          campaigns={campaigns}
        />
      ) : null}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={pending}>
          {copy.cancel}
        </Button>
        {codesFailed ? (
          <Button variant="destructive" disabled={!canSubmit} onClick={() => submit(true)}>
            {copy.continueAnyway}
          </Button>
        ) : (
          <Button variant="destructive" disabled={!canSubmit} onClick={() => submit(false)}>
            {pending ? copy.submitting : copy.submit}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function FailureAlert({
  id,
  failure,
  campaigns,
}: {
  id: string;
  failure: UninstallFailure;
  campaigns: ShopifyUninstallImpactCampaign[];
}) {
  const nameOf = (campaignId: string) =>
    campaigns.find((c) => c.campaign_id === campaignId)?.name ?? copy.unnamedCampaign;
  const ref = useRef<HTMLDivElement>(null);
  const textId = `${id}-text`;
  // Keyed by the caller, so each new failure remounts and takes focus.
  useEffect(() => ref.current?.focus(), []);

  return (
    <Alert
      variant="warning"
      ref={ref}
      tabIndex={-1}
      id={id}
      role="group"
      aria-labelledby={textId}
    >
      <WarningCircle aria-hidden="true" />
      <div id={textId} className="space-y-1">
        <p>{failure.message}</p>
        {failure.codeFailures.length > 0 ? (
          <ul className="list-disc pl-4 text-caption-regular">
            {failure.codeFailures.map((f) => (
              <li key={f.campaignId}>
                {copy.codeFailureLine(nameOf(f.campaignId), f.codeCount, f.reason)}
              </li>
            ))}
          </ul>
        ) : null}
        {failure.codesTurnedOff > 0 ? (
          <p>{copy.codesTurnedOffNote(failure.codesTurnedOff)}</p>
        ) : null}
      </div>
    </Alert>
  );
}

function CampaignsAffected({
  campaigns,
  counts,
  loading,
  error,
}: {
  campaigns: ShopifyUninstallImpactCampaign[];
  counts?: ShopifyUninstallImpact['counts'];
  loading?: boolean;
  error?: boolean;
}) {
  if (loading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-body-sm-regular">
        <LoadingSpinner /> {copy.campaignsLoading}
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="warning">
        <WarningCircle aria-hidden="true" />
        <span>{copy.campaignsError}</span>
      </Alert>
    );
  }
  const visible = campaigns.slice(0, VISIBLE_CAMPAIGNS);
  const extra = campaigns.length - visible.length;
  return (
    <div className="space-y-2">
      {campaigns.length > 0 ? (
        <>
          <p className="text-muted-foreground text-caption-medium tracking-wide uppercase">
            {copy.campaignsHeading(campaigns.length)}
          </p>
          <div className="space-y-2">
            {visible.map((c) => (
              <div
                key={c.campaign_id}
                className="border-border flex items-center justify-between gap-3 rounded-md border px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="shrink-0">
                    <PlatformMark platform={c.platform} size={24} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-foreground truncate text-body-sm-medium">{c.name}</p>
                    <p className="text-muted-foreground text-caption-regular">
                      {copy.creators(c.creator_count)} ·{' '}
                      {shopifyCountsCopy.uninstallCodes(c.coupons, c.active_coupons)}
                    </p>
                  </div>
                </div>
                <StatusPill status={c.status} />
              </div>
            ))}
          </div>
          {extra > 0 && <p className="text-muted-foreground text-caption-regular">and {extra} more</p>}
        </>
      ) : null}
      {counts ? <p className="text-muted-foreground text-caption-regular">{copy.counts(counts)}</p> : null}
    </div>
  );
}
